<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo darken-2">
        <v-toolbar-title>
          <v-icon left small>mdi-clock-check</v-icon>통계 cron 상태
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip x-small outlined color="white" class="mr-2">서버 시각: {{ formatTs(serverNow) }}</v-chip>
        <v-btn icon small dark :loading="loading" @click="load">
          <v-icon small>mdi-reload</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-row dense>
          <!-- statsDaily -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-3">
              <div class="d-flex align-center">
                <v-icon small color="indigo" class="mr-1">mdi-calendar-today</v-icon>
                <span class="text-subtitle-2 indigo--text">statsDaily (매 5분)</span>
                <v-spacer></v-spacer>
                <v-chip x-small :color="statusColor(dailyHb.last_status)" dark v-if="dailyHb.last_status">
                  {{ dailyHb.last_status }}
                </v-chip>
                <v-chip x-small color="grey" v-else>한 번도 발사 안 됨</v-chip>
              </div>
              <v-divider class="my-2"></v-divider>

              <div class="text-caption grey--text">마지막 발사 (heartbeat)</div>
              <div class="text-body-2 font-weight-bold">{{ formatTs(dailyHb.last_fire_at) }}
                <span v-if="dailyHb.last_fire_at" class="text-caption grey--text">({{ minutesAgo(dailyHb.last_fire_at) }}분 전)</span>
              </div>
              <div class="text-caption grey--text mt-1">누적 fire: {{ dailyHb.fire_count || 0 }}회</div>
              <div v-if="dailyHb.last_detail" class="text-caption mt-1">
                <pre class="hb-detail">{{ JSON.stringify(dailyHb.last_detail, null, 2) }}</pre>
              </div>
              <v-alert v-if="dailyHb.last_error" dense type="error" outlined class="mt-2 mb-0">
                {{ dailyHb.last_error }}
              </v-alert>

              <v-divider class="my-2"></v-divider>
              <div class="text-caption grey--text">마지막 DB 집계 시각</div>
              <div class="text-body-2">{{ formatTs(daily.last_aggregated_at) }}</div>
              <div class="text-caption grey--text mt-1">마지막 stat_date: {{ daily.last_stat_date || '-' }}</div>

              <div class="text-caption grey--text mt-3">최근 30일 status</div>
              <div class="d-flex flex-wrap">
                <v-chip x-small class="mr-1 mb-1" :color="statusColor(s.k)" dark
                  v-for="s in statusEntries(daily.status_count_30d)" :key="s.k">
                  {{ s.k }}: {{ s.v }}
                </v-chip>
                <span v-if="!statusEntries(daily.status_count_30d).length" class="text-caption grey--text">없음</span>
              </div>

              <v-divider class="my-3"></v-divider>
              <div class="d-flex align-end">
                <v-text-field dense outlined hide-details type="date"
                  label="date (옵션, 비우면 어제 KST)" v-model="runDate" style="max-width:200px"></v-text-field>
                <v-btn small color="primary" class="ml-2" :loading="running.daily" @click="runDaily">
                  <v-icon small left>mdi-play</v-icon>지금 실행
                </v-btn>
              </div>
              <v-alert v-if="runResult.daily" dense :type="runResult.daily.ok ? 'success' : 'error'" outlined class="mt-2 mb-0 text-caption">
                <pre class="hb-detail mb-0">{{ JSON.stringify(runResult.daily.body, null, 2) }}</pre>
              </v-alert>
            </v-card>
          </v-col>

          <!-- statsMonthly -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-3">
              <div class="d-flex align-center">
                <v-icon small color="deep-purple" class="mr-1">mdi-calendar-month</v-icon>
                <span class="text-subtitle-2 deep-purple--text">statsMonthly (매일 03:00 KST)</span>
                <v-spacer></v-spacer>
                <v-chip x-small :color="statusColor(monthlyHb.last_status)" dark v-if="monthlyHb.last_status">
                  {{ monthlyHb.last_status }}
                </v-chip>
                <v-chip x-small color="grey" v-else>한 번도 발사 안 됨</v-chip>
              </div>
              <v-divider class="my-2"></v-divider>

              <div class="text-caption grey--text">마지막 발사 (heartbeat)</div>
              <div class="text-body-2 font-weight-bold">{{ formatTs(monthlyHb.last_fire_at) }}
                <span v-if="monthlyHb.last_fire_at" class="text-caption grey--text">({{ hoursAgo(monthlyHb.last_fire_at) }}h 전)</span>
              </div>
              <div class="text-caption grey--text mt-1">누적 fire: {{ monthlyHb.fire_count || 0 }}회</div>
              <div v-if="monthlyHb.last_detail" class="text-caption mt-1">
                <pre class="hb-detail">{{ JSON.stringify(monthlyHb.last_detail, null, 2) }}</pre>
              </div>
              <v-alert v-if="monthlyHb.last_error" dense type="error" outlined class="mt-2 mb-0">
                {{ monthlyHb.last_error }}
              </v-alert>

              <v-divider class="my-2"></v-divider>
              <div class="text-caption grey--text">마지막 DB 집계 시각</div>
              <div class="text-body-2">{{ formatTs(monthly.last_aggregated_at) }}</div>
              <div class="text-caption grey--text mt-1">마지막 year_month: {{ monthly.last_year_month || '-' }}</div>

              <v-divider class="my-3"></v-divider>
              <div class="d-flex align-end">
                <v-text-field dense outlined hide-details type="month"
                  label="ym (옵션, 비우면 어제 KST + 직전월)" v-model="runYm" style="max-width:200px"></v-text-field>
                <v-btn small color="deep-purple" dark class="ml-2" :loading="running.monthly" @click="runMonthly">
                  <v-icon small left>mdi-play</v-icon>지금 실행
                </v-btn>
              </div>
              <v-alert v-if="runResult.monthly" dense :type="runResult.monthly.ok ? 'success' : 'error'" outlined class="mt-2 mb-0 text-caption">
                <pre class="hb-detail mb-0">{{ JSON.stringify(runResult.monthly.body, null, 2) }}</pre>
              </v-alert>
            </v-card>
          </v-col>
        </v-row>

        <v-alert v-if="!dailyHb.last_fire_at" dense type="info" outlined class="mt-3 mb-0">
          heartbeat 가 비어있다면 statsDaily 가 이 프로세스에서 아직 한 번도 발사되지 않은 상태입니다. 우측 "지금 실행" 으로 확인하세요. (NODE_APP_INSTANCE=0 인스턴스에서만 cron 발사)
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Today 캐시 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="teal darken-2">
        <v-toolbar-title>Today 캐시 상태</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip x-small outlined color="white">size: {{ todayCache.size || 0 }}</v-chip>
        <v-chip x-small outlined color="white" class="ml-1">in-flight: {{ todayCache.inflight || 0 }}</v-chip>
      </v-toolbar>
      <v-data-table dense :headers="cacheHeaders" :items="cacheEntries" :items-per-page="20" :loading="loading"
        no-data-text="캐시 항목 없음">
        <template v-slot:item.expires_in_ms="{ item }">
          <v-chip x-small :color="item.expires_in_ms < 0 ? 'red' : item.expires_in_ms < 3000 ? 'orange' : 'green'" dark>
            {{ Math.max(0, Math.round(item.expires_in_ms / 1000)) }}s
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <div class="py-12"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'Danta_Stats_CronStatus',
  data: () => ({
    loading: false,
    serverNow: null,
    daily: {},
    monthly: {},
    dailyHb: {},
    monthlyHb: {},
    todayCache: {},
    cacheEntries: [],
    cacheHeaders: [
      { text: '캐시 키', value: 'key' },
      { text: '만료까지', value: 'expires_in_ms', align: 'right' },
    ],
    runDate: '',
    runYm: '',
    running: { daily: false, monthly: false },
    runResult: { daily: null, monthly: null },
  }),
  created() { this.load(); },
  methods: {
    ...mapActions(['DANTA_STATS_CRON_STATUS', 'DANTA_STATS_RUN_DAILY', 'DANTA_STATS_RUN_MONTHLY']),
    formatTs(iso) {
      if (!iso) return '-';
      try {
        const d = new Date(iso);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
      } catch (_) { return String(iso); }
    },
    minutesAgo(iso) { if (!iso) return 0; return Math.round((Date.now() - new Date(iso).getTime()) / 60000); },
    hoursAgo(iso)   { if (!iso) return 0; return Math.round((Date.now() - new Date(iso).getTime()) / 3600000); },
    statusColor(s) {
      if (s === 'ok' || s === 'completed') return 'green';
      if (s === 'failed') return 'red';
      if (s === 'skipped' || s === 'pending') return 'amber';
      return 'grey';
    },
    statusEntries(obj) { if (!obj) return []; return Object.keys(obj).map(k => ({ k, v: obj[k] })); },
    async load() {
      this.loading = true;
      try {
        const r = await this.DANTA_STATS_CRON_STATUS();
        this.daily        = (r && r.daily)        || {};
        this.monthly      = (r && r.monthly)      || {};
        this.dailyHb      = (this.daily.heartbeat)   || {};
        this.monthlyHb    = (this.monthly.heartbeat) || {};
        this.todayCache   = (r && r.today_cache)  || {};
        this.cacheEntries = (this.todayCache.entries) || [];
        this.serverNow    = (r && r.now) || null;
      } catch (err) {
        console.error('[Stats_CronStatus] load 실패:', err);
      } finally {
        this.loading = false;
      }
    },
    async runDaily() {
      this.running.daily = true;
      this.runResult.daily = null;
      try {
        const body = await this.DANTA_STATS_RUN_DAILY({ date: this.runDate || undefined });
        this.runResult.daily = { ok: true, body };
        await this.load();
      } catch (err) {
        this.runResult.daily = { ok: false, body: { error: err.message || String(err) } };
      } finally {
        this.running.daily = false;
      }
    },
    async runMonthly() {
      this.running.monthly = true;
      this.runResult.monthly = null;
      try {
        const body = await this.DANTA_STATS_RUN_MONTHLY({ ym: this.runYm || undefined });
        this.runResult.monthly = { ok: true, body };
        await this.load();
      } catch (err) {
        this.runResult.monthly = { ok: false, body: { error: err.message || String(err) } };
      } finally {
        this.running.monthly = false;
      }
    },
  },
};
</script>

<style scoped>
.hb-detail {
  background: #f5f5f5;
  padding: 4px 6px;
  font-size: 10px;
  line-height: 1.3;
  max-height: 120px;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
